function getVarName(block, fieldName = "VAR", fallback = "x") {
    if (!block) return fallback;
    const field = block.getField(fieldName);
    if (field && typeof field.getText === "function") {
        const txt = field.getText();
        if (txt) return txt;
    }
    return block.getFieldValue(fieldName) || fallback;
}

/**
 * PixelBlock Python Code Generator.
 * Converts ScratchBlocks workspace blocks into clean, PEP-8 styled Python code.
 */

function indentCode(code, spaces = 4) {
    if (!code || !code.trim()) return ' '.repeat(spaces) + 'pass';
    const pad = ' '.repeat(spaces);
    return code
        .split('\n')
        .map(line => (line.trim().length > 0 ? pad + line : ''))
        .join('\n');
}

function getRawOrExprInputValue(block, inputName, defaultValue = "") {
    if (!block) return defaultValue;
    const targetBlock = block.getInputTargetBlock(inputName);
    if (targetBlock) {
        if (targetBlock.type === "text") {
            const raw = targetBlock.getFieldValue("TEXT");
            return raw !== null && raw !== undefined && raw.trim() !== "" ? raw.trim() : defaultValue;
        }
        return blockToPythonExpr(targetBlock);
    }
    const field = block.getField(inputName);
    if (field) {
        const val = field.getValue();
        return val !== null && val !== undefined && String(val).trim() !== "" ? String(val).trim() : defaultValue;
    }
    return defaultValue;
}

function getInputValue(block, inputName, defaultValue = '') {
    if (!block) return defaultValue;
    const targetBlock = block.getInputTargetBlock(inputName);
    if (targetBlock) {
        return blockToPythonExpr(targetBlock);
    }
    const field = block.getField(inputName);
    if (field) {
        return field.getValue();
    }
    return defaultValue;
}

function getSubstackCode(block, substackName = 'SUBSTACK') {
    if (!block) return '';
    const target = block.getInputTargetBlock(substackName);
    if (!target) return '';
    return blockStackToPython(target);
}

export function blockToPythonExpr(block) {
    if (!block) return '';
    const type = block.type;

    switch (type) {
    case 'python_variable_get':
        return getVarName(block, 'VAR', 'x');

    case 'python_number': {
        const val = block.getFieldValue('NUM');
        return val !== null && val !== undefined ? String(val) : '0';
    }

    case 'python_string': {
        const val = block.getFieldValue('TEXT') || '';
        return JSON.stringify(val);
    }

    case 'math_number':
    case 'math_positive_number':
    case 'math_whole_number':
    case 'math_integer': {
        const val = block.getFieldValue('NUM');
        return val !== null && val !== undefined ? String(val) : '0';
    }

    case 'text': {
        let val = block.getFieldValue('TEXT') || '';
        if ((val.startsWith("'") && val.endsWith("'") && val.length >= 2) ||
            (val.startsWith('"') && val.endsWith('"') && val.length >= 2)) {
            val = val.slice(1, -1);
        }
        return JSON.stringify(val);
    }

    case 'python_boolean':
        return block.getFieldValue('BOOL') || 'True';

    case 'python_none':
        return 'None';

    case 'python_compare': {
        const a = getInputValue(block, 'A', '0');
        const op = block.getFieldValue('OP') || '==';
        const b = getInputValue(block, 'B', '0');
        return `(${a} ${op} ${b})`;
    }

    case 'python_bool_op': {
        const a = getInputValue(block, 'A', 'True');
        const op = block.getFieldValue('OP') || 'and';
        const b = getInputValue(block, 'B', 'True');
        return `(${a} ${op} ${b})`;
    }

    case 'python_not': {
        const a = getInputValue(block, 'A', 'False');
        return `(not ${a})`;
    }

    case 'python_arithmetic': {
        const a = getInputValue(block, 'A', '0');
        const op = block.getFieldValue('OP') || '+';
        const b = getInputValue(block, 'B', '0');
        if (block.getParent && block.getParent() && block.getParent().type === 'python_variable_set') {
            return `${a} ${op} ${b}`;
        }
        return `(${a} ${op} ${b})`;
    }

    case 'python_round': {
        const num = getInputValue(block, 'NUM', '0');
        return `round(${num})`;
    }

    case 'python_abs': {
        const num = getInputValue(block, 'NUM', '0');
        return `abs(${num})`;
    }

    case 'python_min_max': {
        const func = block.getFieldValue('FUNC') || 'min';
        const a = getInputValue(block, 'A', '0');
        const b = getInputValue(block, 'B', '0');
        return `${func}(${a}, ${b})`;
    }

    case 'python_unary': {
        const num = getInputValue(block, 'NUM', '0');
        return `(-${num})`;
    }

    case 'python_input': {
        const prompt = getInputValue(block, 'PROMPT', '""');
        return `input(${prompt})`;
    }

    case 'python_input_int': {
        const prompt = getInputValue(block, 'PROMPT', '""');
        return `int(input(${prompt}))`;
    }

    case 'python_input_float': {
        const prompt = getInputValue(block, 'PROMPT', '""');
        return `float(input(${prompt}))`;
    }

    case 'python_cast_int': {
        const val = getInputValue(block, 'VAL', '0');
        return `int(${val})`;
    }

    case 'python_cast_float': {
        const val = getInputValue(block, 'VAL', '0');
        return `float(${val})`;
    }

    case 'python_cast_str': {
        const val = getInputValue(block, 'VAL', '""');
        return `str(${val})`;
    }

    case 'python_cast_bool': {
        const val = getInputValue(block, 'VAL', 'True');
        return `bool(${val})`;
    }

    case 'python_type': {
        const val = getRawOrExprInputValue(block, 'VAL', 'x');
        return `type(${val})`;
    }

    case 'python_list_empty':
        return '[]';

    case 'python_list_create_3': {
        const a = getInputValue(block, 'A', '0');
        const b = getInputValue(block, 'B', '0');
        const c = getInputValue(block, 'C', '0');
        return `[${a}, ${b}, ${c}]`;
    }

    case 'python_list_pop': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const idx = getInputValue(block, 'INDEX', '');
        return idx ? `${list}.pop(${idx})` : `${list}.pop()`;
    }

    case 'python_list_get': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const idx = getInputValue(block, 'INDEX', '0');
        return `${list}[${idx}]`;
    }

    case 'python_list_len': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        return `len(${list})`;
    }

    case 'python_range': {
        const stop = getInputValue(block, 'STOP', '10');
        return `range(${stop})`;
    }

    case 'python_range_start_stop': {
        const start = getInputValue(block, 'START', '0');
        const stop = getInputValue(block, 'STOP', '10');
        return `range(${start}, ${stop})`;
    }

    case 'python_dict_empty':
        return '{}';

    case 'python_dict_create': {
        const k1 = getInputValue(block, 'KEY1', '"a"');
        const v1 = getInputValue(block, 'VAL1', '1');
        const k2 = getInputValue(block, 'KEY2', '"b"');
        const v2 = getInputValue(block, 'VAL2', '2');
        return `{${k1}: ${v1}, ${k2}: ${v2}}`;
    }

    case 'python_dict_get': {
        const dict = getInputValue(block, 'DICT', 'my_dict');
        const key = getInputValue(block, 'KEY', '"key"');
        return `${dict}[${key}]`;
    }

    case 'python_dict_keys': {
        const dict = getInputValue(block, 'DICT', 'my_dict');
        return `${dict}.keys()`;
    }

    case 'python_dict_values': {
        const dict = getInputValue(block, 'DICT', 'my_dict');
        return `${dict}.values()`;
    }

    case 'python_call_expr': {
        const name = block.getFieldValue('NAME') || 'my_function';
        return `${name}()`;
    }

    case 'python_call_expr_arg': {
        const name = block.getFieldValue('NAME') || 'my_function';
        const arg = getInputValue(block, 'ARG', '');
        return `${name}(${arg})`;
    }

    case 'python_raw_expr': {
        return block.getFieldValue('CODE') || 'None';
    }

    default:
        // Fallback for native Scratch reporter blocks if any exist
        if (block.getFieldValue) {
            const val = block.getFieldValue('TEXT') || block.getFieldValue('NUM') || block.getFieldValue('VALUE');
            if (val !== null && val !== undefined) return String(val);
        }
        return 'None';
    }
}

export function blockToPythonStmt(block) {
    if (!block) return '';
    const type = block.type;

    switch (type) {
    case 'python_variable_set': {
        const varName = getVarName(block, 'VAR', 'x');
        const val = getInputValue(block, 'VALUE', '0');
        return `${varName} = ${val}`;
    }

    case 'python_variable_change': {
        const varName = getVarName(block, 'VAR', 'x');
        const op = block.getFieldValue('OP') || '+';
        const val = getInputValue(block, 'VALUE', '1');
        return `${varName} ${op}= ${val}`;
    }

    case 'python_if':
    case 'python_ifelse': {
        const cond = getInputValue(block, 'CONDITION', 'True');
        const body = getSubstackCode(block, 'SUBSTACK');
        let code = `if ${cond}:\n${indentCode(body)}`;

        let i = 1;
        while (block.getInput && block.getInput(`CONDITION${i}`)) {
            const elifCond = getInputValue(block, `CONDITION${i}`, 'True');
            const elifBody = getSubstackCode(block, `SUBSTACK${i}`);
            code += `\nelif ${elifCond}:\n${indentCode(elifBody)}`;
            i++;
        }

        if (block.hasElse_ || (block.getInput && (block.getInput('SUBSTACK_ELSE') || block.getInput('SUBSTACK2')))) {
            const elseBody = getSubstackCode(block, 'SUBSTACK_ELSE') || getSubstackCode(block, 'SUBSTACK2');
            code += `\nelse:\n${indentCode(elseBody)}`;
        }

        return code;
    }

    case 'python_if_elif': {
        const cond1 = getInputValue(block, 'CONDITION1', 'True');
        const body1 = getSubstackCode(block, 'SUBSTACK1');
        const cond2 = getInputValue(block, 'CONDITION2', 'True');
        const body2 = getSubstackCode(block, 'SUBSTACK2');
        return `if ${cond1}:\n${indentCode(body1)}\nelif ${cond2}:\n${indentCode(body2)}`;
    }

    case 'python_if_elif_else': {
        const cond1 = getInputValue(block, 'CONDITION1', 'True');
        const body1 = getSubstackCode(block, 'SUBSTACK1');
        const cond2 = getInputValue(block, 'CONDITION2', 'True');
        const body2 = getSubstackCode(block, 'SUBSTACK2');
        const body3 = getSubstackCode(block, 'SUBSTACK3');
        return `if ${cond1}:\n${indentCode(body1)}\nelif ${cond2}:\n${indentCode(body2)}\nelse:\n${indentCode(body3)}`;
    }

    case 'python_if_elif_elif_else': {
        const cond1 = getInputValue(block, 'CONDITION1', 'True');
        const body1 = getSubstackCode(block, 'SUBSTACK1');
        const cond2 = getInputValue(block, 'CONDITION2', 'True');
        const body2 = getSubstackCode(block, 'SUBSTACK2');
        const cond3 = getInputValue(block, 'CONDITION3', 'True');
        const body3 = getSubstackCode(block, 'SUBSTACK3');
        const body4 = getSubstackCode(block, 'SUBSTACK4');
        return `if ${cond1}:\n${indentCode(body1)}\nelif ${cond2}:\n${indentCode(body2)}\nelif ${cond3}:\n${indentCode(body3)}\nelse:\n${indentCode(body4)}`;
    }

    case 'python_for_range': {
        const v = block.getFieldValue('VAR') || 'i';
        const start = getInputValue(block, 'START', '0');
        const stop = getInputValue(block, 'STOP', '10');
        const body = getSubstackCode(block, 'SUBSTACK');
        return `for ${v} in range(${start}, ${stop}):\n${indentCode(body)}`;
    }

    case 'python_for_range_step': {
        const v = block.getFieldValue('VAR') || 'i';
        const start = getInputValue(block, 'START', '0');
        const stop = getInputValue(block, 'STOP', '10');
        const step = getInputValue(block, 'STEP', '1');
        const body = getSubstackCode(block, 'SUBSTACK');
        return `for ${v} in range(${start}, ${stop}, ${step}):\n${indentCode(body)}`;
    }

    case 'python_for_in': {
        const v = block.getFieldValue('VAR') || 'item';
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const body = getSubstackCode(block, 'SUBSTACK');
        return `for ${v} in ${list}:\n${indentCode(body)}`;
    }

    case 'python_while': {
        const cond = getRawOrExprInputValue(block, 'CONDITION', 'True');
        const body = getSubstackCode(block, 'SUBSTACK');
        return `while ${cond}:\n${indentCode(body)}`;
    }

    case 'python_break':
        return 'break';

    case 'python_continue':
        return 'continue';

    case 'python_pass':
        return 'pass';

    case 'python_print': {
        const items = [];
        for (let i = 0; ; i++) {
            const inputName = i === 0 ? 'TEXT' : ('TEXT' + i);
            if (block.getInput(inputName)) {
                items.push(getInputValue(block, inputName, '""'));
            } else {
                break;
            }
        }
        if (items.length === 0) {
            return 'print("")';
        }
        return `print(${items.join(", ")})`;
    }

    case 'python_print_multi': {
        const a = getInputValue(block, 'A', '""');
        const b = getInputValue(block, 'B', '""');
        return `print(${a}, ${b})`;
    }

    case 'python_print_three': {
        const a = getInputValue(block, 'A', '""');
        const b = getInputValue(block, 'B', '""');
        const c = getInputValue(block, 'C', '""');
        return `print(${a}, ${b}, ${c})`;
    }

    case 'python_print_four': {
        const a = getInputValue(block, 'A', '""');
        const b = getInputValue(block, 'B', '""');
        const c = getInputValue(block, 'C', '""');
        const d = getInputValue(block, 'D', '""');
        return `print(${a}, ${b}, ${c}, ${d})`;
    }

    case 'python_print_five': {
        const a = getInputValue(block, 'A', '""');
        const b = getInputValue(block, 'B', '""');
        const c = getInputValue(block, 'C', '""');
        const d = getInputValue(block, 'D', '""');
        const e = getInputValue(block, 'E', '""');
        return `print(${a}, ${b}, ${c}, ${d}, ${e})`;
    }

    case 'python_print_end': {
        const text = getInputValue(block, 'TEXT', '""');
        const end = getInputValue(block, 'END', '""');
        return `print(${text}, end=${end})`;
    }

    case 'python_list_append': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const item = getInputValue(block, 'ITEM', '0');
        return `${list}.append(${item})`;
    }

    case 'python_list_remove': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const item = getInputValue(block, 'ITEM', '0');
        return `${list}.remove(${item})`;
    }

    case 'python_list_insert': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const idx = getInputValue(block, 'INDEX', '0');
        const item = getInputValue(block, 'ITEM', '0');
        return `${list}.insert(${idx}, ${item})`;
    }

    case 'python_list_set': {
        const list = getRawOrExprInputValue(block, 'LIST', 'my_list');
        const idx = getInputValue(block, 'INDEX', '0');
        const val = getInputValue(block, 'VALUE', '0');
        return `${list}[${idx}] = ${val}`;
    }

    case 'python_dict_set': {
        const dict = getInputValue(block, 'DICT', 'my_dict');
        const key = getInputValue(block, 'KEY', '"key"');
        const val = getInputValue(block, 'VAL', '0');
        return `${dict}[${key}] = ${val}`;
    }

    case 'python_def': {
        const name = block.getFieldValue('NAME') || 'my_function';
        const body = getSubstackCode(block, 'SUBSTACK');
        return `def ${name}():\n${indentCode(body)}`;
    }

    case 'python_def_one_arg': {
        const name = block.getFieldValue('NAME') || 'my_function';
        const arg1 = block.getFieldValue('ARG1') || 'arg';
        const body = getSubstackCode(block, 'SUBSTACK');
        return `def ${name}(${arg1}):\n${indentCode(body)}`;
    }

    case 'python_def_two_args': {
        const name = block.getFieldValue('NAME') || 'my_function';
        const arg1 = block.getFieldValue('ARG1') || 'a';
        const arg2 = block.getFieldValue('ARG2') || 'b';
        const body = getSubstackCode(block, 'SUBSTACK');
        return `def ${name}(${arg1}, ${arg2}):\n${indentCode(body)}`;
    }

    case 'python_return': {
        const val = getInputValue(block, 'VAL', '');
        return val ? `return ${val}` : 'return';
    }

    case 'python_call_stmt': {
        const name = block.getFieldValue('NAME') || 'my_function';
        return `${name}()`;
    }

    case 'python_call_stmt_arg': {
        const name = block.getFieldValue('NAME') || 'my_function';
        const arg = getInputValue(block, 'ARG', '');
        return `${name}(${arg})`;
    }

    case 'python_turtle_forward': {
        const d = getInputValue(block, 'DIST', '100');
        return `turtle.forward(${d})`;
    }

    case 'python_turtle_backward': {
        const d = getInputValue(block, 'DIST', '100');
        return `turtle.backward(${d})`;
    }

    case 'python_turtle_right': {
        const a = getInputValue(block, 'ANGLE', '90');
        return `turtle.right(${a})`;
    }

    case 'python_turtle_left': {
        const a = getInputValue(block, 'ANGLE', '90');
        return `turtle.left(${a})`;
    }

    case 'python_turtle_goto': {
        const x = getInputValue(block, 'X', '0');
        const y = getInputValue(block, 'Y', '0');
        return `turtle.goto(${x}, ${y})`;
    }

    case 'python_turtle_setheading': {
        const a = getInputValue(block, 'ANGLE', '0');
        return `turtle.setheading(${a})`;
    }

    case 'python_turtle_penup':
        return 'turtle.penup()';

    case 'python_turtle_pendown':
        return 'turtle.pendown()';

    case 'python_turtle_color': {
        const c = getInputValue(block, 'COLOR', '"blue"');
        return `turtle.pencolor(${c})`;
    }

    case 'python_turtle_pensize': {
        const s = getInputValue(block, 'SIZE', '2');
        return `turtle.pensize(${s})`;
    }

    case 'python_turtle_circle': {
        const r = getInputValue(block, 'RADIUS', '50');
        return `turtle.circle(${r})`;
    }

    case 'python_turtle_speed': {
        const s = getInputValue(block, 'SPEED', '3');
        return `turtle.speed(${s})`;
    }

    case 'python_turtle_clear':
        return 'turtle.clear()';

    case 'python_turtle_reset':
        return 'turtle.reset()';

    case 'python_turtle_mainloop':
        return 'turtle.mainloop()';

    case 'python_import': {
        const m = block.getFieldValue('MODULE') || 'math';
        return `import ${m}`;
    }

    case 'python_from_import': {
        const m = block.getFieldValue('MODULE') || 'random';
        const item = block.getFieldValue('ITEM') || 'randint';
        return `from ${m} import ${item}`;
    }

    case 'python_import_as': {
        const m = block.getFieldValue('MODULE') || 'numpy';
        const a = block.getFieldValue('ALIAS') || 'np';
        return `import ${m} as ${a}`;
    }

    case 'python_class': {
        const name = block.getFieldValue('NAME') || 'MyClass';
        const body = getSubstackCode(block, 'SUBSTACK');
        return `class ${name}:\n${indentCode(body)}`;
    }

    case 'python_comment': {
        const c = block.getFieldValue('COMMENT') || '';
        return `# ${c}`;
    }

    case 'python_raw': {
        return block.getFieldValue('CODE') || 'pass';
    }

    default:
        // If it's an expression block placed as a statement
        return blockToPythonExpr(block);
    }
}

export function blockStackToPython(startBlock) {
    const lines = [];
    let cur = startBlock;
    while (cur) {
        const stmt = blockToPythonStmt(cur);
        if (stmt) {
            lines.push(stmt);
        }
        cur = cur.getNextBlock ? cur.getNextBlock() : null;
    }
    return lines.join('\n');
}

export function workspaceToPython(workspace) {
    if (!workspace) return '# PixelBlock v6 Python Studio\n';

    const topBlocks = workspace.getTopBlocks ? workspace.getTopBlocks(true) : [];
    // Sort top blocks by vertical Y coordinate
    topBlocks.sort((a, b) => {
        const posA = a.getRelativeToSurfaceXY ? a.getRelativeToSurfaceXY() : { y: 0 };
        const posB = b.getRelativeToSurfaceXY ? b.getRelativeToSurfaceXY() : { y: 0 };
        return posA.y - posB.y;
    });

    const segments = [];
    let usesTurtle = false;

    for (const block of topBlocks) {
        if (!block || block.isShadow()) continue;
        if (block.getParent && block.getParent()) continue;
        if (block.outputConnection && block.outputConnection.targetConnection) continue;
        const code = blockStackToPython(block);
        if (code && code.trim()) {
            if (code.includes('turtle.')) {
                usesTurtle = true;
            }
            segments.push(code);
        }
    }

    let result = '';
    if (usesTurtle && !segments.some(s => s.startsWith('import turtle'))) {
        result += 'import turtle\n\n';
    }

    result += segments.join('\n\n');
    return result || '# PixelBlock Python Code\n# Drag blocks to the workspace to generate code.\n';
}
